import {
  HHAlert,
  HHBox,
  HHCircularProgress,
  HHSelect,
  HHSelectChangeEvent,
  HHStack,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableContainer,
  HHTableHead,
  HHTableRow,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useState } from 'react';
import {
  DEFAULT_PAGE_SIZE,
  FILE_PARSING_BATCH_STATUS_LABELS,
} from '../constants/partner-data-gateway.constant';
import { usePermissions } from '../context/permissions-context';
import { BatchHeatmapCell } from '../domain/types';
import { useIngestionBatchSelector } from '../hooks/use-ingestion-batches';
import { useIngestionRecords } from '../hooks/use-ingestion-records';
import { formatCount, truncateId } from '../utils/format.utils';
import { printDateAndTime } from '../utils/print-date-and-time';
import { CursorPagination } from './cursor-pagination';
import { JsonViewer } from './json-viewer';

export const RECORDS_TAB_TEST_ID = 'ingestion-records-tab';
export const RECORDS_TAB_NO_PHI_TEST_ID = 'ingestion-records-no-phi';
export const RECORDS_TAB_PHI_WARNING_TEST_ID = 'ingestion-records-phi-warning';
export const RECORDS_TAB_BATCH_SELECT_TEST_ID =
  'ingestion-records-batch-select';
export const RECORDS_TAB_TABLE_TEST_ID = 'ingestion-records-table';
export const RECORDS_TAB_ROW_TEST_ID_PREFIX = 'ingestion-records-table-row';
export const RECORDS_TAB_BATCHES_LOADING_TEST_ID =
  'ingestion-records-batches-loading';
export const RECORDS_TAB_BATCHES_ERROR_TEST_ID =
  'ingestion-records-batches-error';
export const RECORDS_TAB_NO_BATCHES_TEST_ID = 'ingestion-records-no-batches';
export const RECORDS_TAB_RECORDS_LOADING_TEST_ID =
  'ingestion-records-records-loading';
export const RECORDS_TAB_RECORDS_ERROR_TEST_ID = 'ingestion-records-error';
export const RECORDS_TAB_EMPTY_TEST_ID = 'ingestion-records-empty';

export const RECORDS_TAB_NO_PHI_MESSAGE =
  'You do not have permission to view record data. PHI access is required.';
export const RECORDS_TAB_PHI_WARNING_MESSAGE =
  '⚠️ This tab displays Protected Health Information (PHI). Access is logged.';
export const RECORDS_TAB_RECORDS_ERROR_MESSAGE =
  'Unable to load records for this batch. Records require PHI access — if you have it, please try again.';
export const RECORDS_TAB_RAW_DATA_LABEL = 'View';

const BATCH_SELECT_WIDTH = 420;
const BATCH_OPTION_ID_LENGTH = 12;

export interface RecordsTabProps {
  eventId: string;
}

/** `batch-1 · Completed · 100 rows` — enough to pick a batch at a glance. */
const buildBatchOptionLabel = (batch: BatchHeatmapCell): string =>
  [
    truncateId(batch.batchId, BATCH_OPTION_ID_LENGTH),
    FILE_PARSING_BATCH_STATUS_LABELS[batch.status],
    `${formatCount(batch.rowCount)} rows`,
  ].join(' · ');

const Spinner = ({ testId }: { testId: string }): JSX.Element => (
  <HHBox
    data-testid={testId}
    display="flex"
    justifyContent="center"
    paddingY={4}
  >
    <HHCircularProgress />
  </HHBox>
);

const PhiWarningBanner = (): JSX.Element => (
  <HHBox
    data-testid={RECORDS_TAB_PHI_WARNING_TEST_ID}
    marginBottom={2}
    paddingX={2}
    paddingY={1}
    sx={{ backgroundColor: 'warning.light', borderRadius: 1 }}
  >
    <HHTypography
      hhVariant="variant-bypass"
      variant="body2"
      sx={{ color: 'warning.dark' }}
    >
      {RECORDS_TAB_PHI_WARNING_MESSAGE}
    </HHTypography>
  </HHBox>
);

interface RecordsTableProps {
  eventId: string;
  batchId: string;
}

const RecordsTable = ({ eventId, batchId }: RecordsTableProps): JSX.Element => {
  const {
    records,
    isLoading,
    hasError,
    hasNext,
    hasPrevious,
    currentPage,
    goToNext,
    goToPrevious,
  } = useIngestionRecords(eventId, { batchId, limit: DEFAULT_PAGE_SIZE });

  if (isLoading) {
    return <Spinner testId={RECORDS_TAB_RECORDS_LOADING_TEST_ID} />;
  }

  // `pdgIngestionRecords` is guarded by `GqlPhiGuard`, so a user who reaches
  // this table without PHI access gets a GraphQL error instead of data.
  // testid sits on the wrapper: HHAlert does not forward unknown props.
  if (hasError) {
    return (
      <HHBox data-testid={RECORDS_TAB_RECORDS_ERROR_TEST_ID} paddingY={2}>
        <HHAlert hhVariant="outlined" severity="error">
          {RECORDS_TAB_RECORDS_ERROR_MESSAGE}
        </HHAlert>
      </HHBox>
    );
  }

  if (records.length === 0) {
    return (
      <HHBox
        data-testid={RECORDS_TAB_EMPTY_TEST_ID}
        sx={{ textAlign: 'center', paddingY: 8 }}
      >
        <HHTypography hhVariant="variant-bypass" variant="body1">
          No records found for this batch.
        </HHTypography>
      </HHBox>
    );
  }

  return (
    <HHBox>
      <HHTableContainer data-testid={RECORDS_TAB_TABLE_TEST_ID}>
        <HHTable aria-label="Parsed file records">
          <HHTableHead>
            <HHTableRow>
              <HHTableCell>Row Index</HHTableCell>
              <HHTableCell>Record ID</HHTableCell>
              <HHTableCell>Raw Data</HHTableCell>
              <HHTableCell>Created</HHTableCell>
            </HHTableRow>
          </HHTableHead>
          <HHTableBody>
            {records.map(record => (
              <HHTableRow
                key={record.id}
                hover
                data-testid={`${RECORDS_TAB_ROW_TEST_ID_PREFIX}-${record.id}`}
              >
                <HHTableCell>{record.originalRowIndex}</HHTableCell>
                <HHTableCell>
                  {/* Focusable so the full id is reachable by keyboard, not
                      just hover, and labelled so a screen reader gets the
                      whole value rather than the truncation. Mirrors the
                      focusable-cell pattern in batch-heatmap.tsx. */}
                  <HHTooltip hhVariant="variant-bypass" title={record.id}>
                    <HHBox tabIndex={0} aria-label={record.id}>
                      <HHTypography hhVariant="variant-bypass" variant="body2">
                        {truncateId(record.id)}
                      </HHTypography>
                    </HHBox>
                  </HHTooltip>
                </HHTableCell>
                <HHTableCell>
                  {/* Collapsed by default so PHI is not on screen until the
                      operator explicitly asks for it. */}
                  <JsonViewer
                    value={record.rawData}
                    label={RECORDS_TAB_RAW_DATA_LABEL}
                  />
                </HHTableCell>
                <HHTableCell>{printDateAndTime(record.createdAt)}</HHTableCell>
              </HHTableRow>
            ))}
          </HHTableBody>
        </HHTable>
      </HHTableContainer>

      <HHBox marginTop={2}>
        <CursorPagination
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          onPrevious={goToPrevious}
          onNext={goToNext}
          currentPage={currentPage}
        />
      </HHBox>
    </HHBox>
  );
};

const RecordsTabContent = ({ eventId }: RecordsTabProps): JSX.Element => {
  const {
    batches,
    isLoading: areBatchesLoading,
    hasError: hasBatchesError,
  } = useIngestionBatchSelector(eventId);
  const [chosenBatchId, setChosenBatchId] = useState('');
  // `batchId` is required by the API, so fall back to the first batch until
  // the operator picks one — derived rather than stored in an effect, so the
  // selection cannot lag a batches refetch.
  const selectedBatchId = chosenBatchId || batches[0]?.batchId || '';

  return (
    <HHBox data-testid={RECORDS_TAB_TEST_ID}>
      <PhiWarningBanner />

      {areBatchesLoading && (
        <Spinner testId={RECORDS_TAB_BATCHES_LOADING_TEST_ID} />
      )}

      {!areBatchesLoading && hasBatchesError && (
        <HHBox data-testid={RECORDS_TAB_BATCHES_ERROR_TEST_ID} paddingY={2}>
          <HHAlert hhVariant="outlined" severity="error">
            Unable to load the batches for this file. Please try again.
          </HHAlert>
        </HHBox>
      )}

      {!areBatchesLoading && !hasBatchesError && batches.length === 0 && (
        <HHBox
          data-testid={RECORDS_TAB_NO_BATCHES_TEST_ID}
          sx={{ textAlign: 'center', paddingY: 8 }}
        >
          <HHTypography hhVariant="variant-bypass" variant="body1">
            This file has no parsing batches yet.
          </HHTypography>
        </HHBox>
      )}

      {batches.length > 0 && (
        <HHStack gap={2}>
          <HHSelect
            data-testid={RECORDS_TAB_BATCH_SELECT_TEST_ID}
            label="Batch"
            width={BATCH_SELECT_WIDTH}
            selectOptions={batches.map(batch => ({
              value: batch.batchId,
              displayName: buildBatchOptionLabel(batch),
            }))}
            initialSelectValue={selectedBatchId}
            onChange={(event: HHSelectChangeEvent): void =>
              setChosenBatchId(String(event.target.value))
            }
            inputProps={{ role: 'combobox', name: 'batchId' }}
          />

          <RecordsTable eventId={eventId} batchId={selectedBatchId} />
        </HHStack>
      )}
    </HHBox>
  );
};

/**
 * Raw parsed rows for one batch of a file, for auditing what PDGS actually
 * read. `rawData` is PHI, so the whole tab is gated on `canViewPhi`: the
 * detail page keeps it out of the tab list, and this guard repeats the check
 * so a deep link to `?tab=records` cannot slip past it.
 */
export const RecordsTab = ({ eventId }: RecordsTabProps): JSX.Element => {
  const { canViewPhi } = usePermissions();

  if (!canViewPhi) {
    return (
      <HHBox paddingY={4} data-testid={RECORDS_TAB_NO_PHI_TEST_ID}>
        <HHTypography hhVariant="variant-bypass" variant="body1">
          {RECORDS_TAB_NO_PHI_MESSAGE}
        </HHTypography>
      </HHBox>
    );
  }

  return <RecordsTabContent eventId={eventId} />;
};
